import ReactAnd from 'react-and';
import { Button } from '@components/Clickables/Buttons/Button';
import { InternalLink } from '@components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '@components/FAQ/FAQ';
import { getFishingWaterUrl } from '@domain/routes/urls';
import { CountryDetailFields, FishingWaterRegionFields } from '@generated/graphql';
import { addDefiniteArticleToCountryName } from '@domain/country/addDefiniteArticleToCountryName';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';

interface IProps {
  country: CountryDetailFields | null;
  handleCTAClick: () => void;
  region?: FishingWaterRegionFields | null;
  topSpecies?: { _id: string; displayName: string; url: string }[];
  topFishingWaters?: { _id: string; displayName: string }[];
  hasRegulations?: boolean;
}

const NUMBER_OF_FISHING_WATERS_TO_SHOW = 3;
const NUMBER_OF_FISHING_SPECIES_TO_SHOW = 3;

export const getFAQData = ({
  country,
  handleCTAClick,
  region,
  topSpecies,
  topFishingWaters,
  hasRegulations,
}: IProps): IFAQData[] => {
  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionName = firstNonEmpty(region?.localName, region?.name) || undefined;

  const countryNameWithDefiniteArticle =
    countryName && countryId ? addDefiniteArticleToCountryName(countryName, countryId) : undefined;
  const countryNameWithDefiniteArticleOrCountryName = !regionName
    ? countryNameWithDefiniteArticle
    : countryName;
  return [
    ...(hasRegulations
      ? [
          {
            id: '1',
            title: `📜 What are the fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName}?`,
            body: (
              <>
                {`Fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} vary by location, species, and season. Rules may include size limits, bag limits, licensing requirements, and protected areas. Because regulations are managed locally and can change throughout the year, it's important to check the latest official guidance before fishing.`}
                <br />
                <br />
                {`To stay up to date and avoid guesswork, use the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` to explore local rules, discover popular waters, and see what other anglers are catching across ${countryNameWithDefiniteArticleOrCountryName}.`}
              </>
            ),
            bodyString: `Fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} vary by region, species, and season. Rules may include size limits, bag limits, licensing requirements, and protected areas. Because regulations are managed locally and can change throughout the year, it's important to check the latest official guidance before fishing. To stay up to date and avoid guesswork, use the Fishbrain app to explore local rules, discover popular waters, and see what other anglers are catching across ${countryNameWithDefiniteArticleOrCountryName}.`,
          },
        ]
      : []),
    ...(topSpecies && topSpecies.length > 0
      ? [
          {
            id: '2',
            title: `🐟 What are the best fish species to catch in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName}?`,
            body: (
              <>
                {`Some of the most popular species anglers target in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} include `}
                <ReactAnd>
                  {topSpecies
                    .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
                    .map(({ displayName, _id, url }) => {
                      return (
                        <InternalLink
                          key={`species-${_id}`}
                          to={url}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. The best species often depend on season, water type, and location within the country.`}
                <br />
                <br />
                {`For more detailed, real-time insights, the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` shows which species are being caught near you and what baits are working best across ${countryNameWithDefiniteArticleOrCountryName}.`}
              </>
            ),
            bodyString: `Some of the most popular species anglers target in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} include ${topSpecies
              .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
              // nosemgrep: javascript.lang.security.html-in-template-string
              .map(({ displayName, url }) => `<a href="${url}">${displayName}</a>`)
              .join(
                ', ',
              )}. The best species often depend on season, water type, and location within the country. For more detailed, real-time insights, the Fishbrain app shows which species are being caught near you and what baits are working best across ${countryNameWithDefiniteArticleOrCountryName}.`,
          },
        ]
      : []),
    ...(topFishingWaters && topFishingWaters.length > 0
      ? [
          {
            id: '3',
            title: `🌊 Where are the top fishing spots in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName}?`,
            body: (
              <>
                {`Some of the most popular fishing spots in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} include `}
                <ReactAnd>
                  {topFishingWaters
                    .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
                    .flatMap(({ displayName, _id }) => {
                      return (
                        <InternalLink
                          key={`fishing-water-${_id}`}
                          to={getFishingWaterUrl(_id, displayName)}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. Anglers across the country fish a wide range of waters, with opportunities varying by region and season.`}
                <br />
                <br />
                {`With the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {`, you can explore local hotspots anywhere in ${countryNameWithDefiniteArticleOrCountryName}, see recent catches, and find out what's working near you.`}
              </>
            ),
            bodyString: `Some of the most popular fishing spots in ${regionName ? `${regionName}, ` : ''}${countryNameWithDefiniteArticleOrCountryName} include ${topFishingWaters
              .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
              .flatMap(({ displayName, _id }) => {
                // nosemgrep: javascript.lang.security.html-in-template-string
                return `<a href="${getFishingWaterUrl(_id, displayName)}">${displayName}</a>`;
              })
              .join(
                ', ',
              )}. Anglers across the country fish a wide range of waters, with opportunities varying by region and season. With the Fishbrain app, you can explore local hotspots anywhere in ${countryNameWithDefiniteArticleOrCountryName}, see recent catches, and find out what's working near you.`,
          },
        ]
      : []),
  ];
};
